import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  DocumentationFileIds,
  DocumentationSlots,
  DraftMetadataFileEntry
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  buildDocumentationPageRows,
  DOCUMENTATION_SLOT_DEFINITIONS,
  DOCUMENTATION_SLOT_FILE_ACCEPT,
  DOCUMENTATION_SLOT_KEYS,
  isFullPageSelection,
  normalizePageList
} from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import styles from './ProjectAutofillCard.module.scss'

const parsePageSegments = (raw: string, maxPage: number): number[] => {
  const out = new Set<number>()
  const parts = raw
    .split(/[,;\n]+/)
    .map((x) => x.trim())
    .filter(Boolean)
  for (const part of parts) {
    const range = part.match(/^(\d+)\s*-\s*(\d+)$/)
    if (range) {
      const a = Number(range[1])
      const b = Number(range[2])
      if (!Number.isFinite(a) || !Number.isFinite(b)) continue
      const lo = Math.min(Math.floor(a), Math.floor(b))
      const hi = Math.max(Math.floor(a), Math.floor(b))
      for (let p = lo; p <= hi; p++) {
        if (p >= 1 && p <= maxPage) out.add(p)
      }
    } else {
      const n = Number(part)
      if (!Number.isFinite(n)) continue
      const p = Math.floor(n)
      if (p >= 1 && p <= maxPage) out.add(p)
    }
  }
  return Array.from(out).sort((a, b) => a - b)
}

const fileEntryLabel = (f: DraftMetadataFileEntry) => {
  const n = f.name || f.originalName
  if (n && String(n).trim()) return String(n).trim()
  const id = f.id != null ? String(f.id) : ''
  return id.length > 8 ? `${id.slice(0, 6)}…` : id || 'Archivo'
}

const summarizePagesShort = (pages: number[] | undefined, maxItems = 12): string => {
  const norm = normalizePageList(pages)
  if (!norm?.length) return ''
  if (norm.length <= maxItems) return norm.join(', ')
  return `${norm.slice(0, maxItems).join(', ')}… (+${norm.length - maxItems})`
}

type SlotKey = (typeof DOCUMENTATION_SLOT_KEYS)[number]

type SlotFileTriggerProps = {
  disabled: boolean
  isUploading: boolean
  onFile: (file: File) => void
}

const RubroDocSlotFileTrigger: React.FC<SlotFileTriggerProps> = ({ disabled, isUploading, onFile }) => {
  const inputRef = useRef<HTMLInputElement>(null)
  return (
    <>
      <button
        type="button"
        className={styles.projectAutofillRubroDocUploadBtn}
        disabled={disabled || isUploading}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          inputRef.current?.click()
        }}
      >
        {isUploading ? 'Subiendo…' : 'Subir'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={DOCUMENTATION_SLOT_FILE_ACCEPT}
        disabled={disabled || isUploading}
        className={styles.projectAutofillRubroDocFileInput}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) onFile(f)
        }}
      />
    </>
  )
}

type PagePickerProps = {
  maxPage: number
  value: number[] | undefined
  pdfBaseUrl?: string
  onChange: (pages: number[]) => void
  showSelectedChips?: boolean
}

const DocumentationSlotPagePicker: React.FC<PagePickerProps> = ({
  maxPage,
  value,
  pdfBaseUrl,
  onChange,
  showSelectedChips = true
}) => {
  const selected = useMemo(() => new Set(normalizePageList(value) ?? []), [value])
  const sorted = useMemo(() => Array.from(selected).sort((a, b) => a - b), [selected])
  const allPages = useMemo(() => Array.from({ length: maxPage }, (_, i) => i + 1), [maxPage])
  const [addText, setAddText] = useState('')
  const basePdf = pdfBaseUrl?.trim() || ''

  const applyAddText = () => {
    const parsed = parsePageSegments(addText, maxPage)
    if (!parsed.length) return
    const next = new Set(selected)
    for (const p of parsed) next.add(p)
    onChange(Array.from(next).sort((a, b) => a - b))
    setAddText('')
  }

  const remove = (p: number) => {
    const next = new Set(selected)
    next.delete(p)
    onChange(Array.from(next).sort((a, b) => a - b))
  }

  return (
    <div className={styles.projectAutofillRubroDocPagePicker}>
      {showSelectedChips ? (
        <div className={styles.projectAutofillRubroDocPagePickerChips} aria-label="Páginas elegidas">
          {sorted.length === 0 ? (
            <span className={styles.projectAutofillRubroDocPagePickerEmpty}>Todavía no elegiste páginas.</span>
          ) : (
            sorted.map((p) => (
              <span key={p} className={styles.projectAutofillRubroDocPagePickerChip}>
                {basePdf ? (
                  <a
                    href={`${basePdf}#page=${p}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.projectAutofillRubroDocPagePickerChipLink}
                  >
                    Página{' '}
                    <span className={styles.projectAutofillRubroDocPagePickerChipNum}>{p}</span>
                  </a>
                ) : null}
                <button
                  type="button"
                  className={styles.projectAutofillRubroDocPagePickerChipRemove}
                  onClick={() => remove(p)}
                  aria-label={`Quitar página ${p}`}
                >
                  ×
                </button>
              </span>
            ))
          )}
        </div>
      ) : (
        <p className={styles.projectAutofillRubroDocMuted}>
          Las páginas que agregues para este tipo aparecen en el listado superior con su etiqueta.
        </p>
      )}

      <div className={styles.projectAutofillRubroDocPagePickerAddRow}>
        <input
          type="text"
          className={styles.projectAutofillRubroDocPagePickerInput}
          value={addText}
          onChange={(e) => setAddText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              applyAddText()
            }
          }}
          placeholder={`Páginas del 1 al ${maxPage} (ej. 2, 5, 10-14)`}
        />
        <button type="button" className={styles.projectAutofillRubroDocPagePickerAddBtn} onClick={applyAddText}>
          Agregar
        </button>
      </div>

      <div className={styles.projectAutofillRubroDocPagePickerQuick}>
        <button type="button" className={styles.projectAutofillRubroDocPagePickerQuickBtn} onClick={() => onChange([...allPages])}>
          Elegir todas ({maxPage})
        </button>
        <span className={styles.projectAutofillRubroDocPagePickerQuickSep}>·</span>
        <button type="button" className={styles.projectAutofillRubroDocPagePickerQuickBtn} onClick={() => onChange([])}>
          Quitar todas
        </button>
      </div>
    </div>
  )
}

type Props = {
  tempId: string
  slots: DocumentationSlots
  sourcePages?: number[]
  pdfBaseUrl?: string
  maxPage?: number
  fileIds: DocumentationFileIds
  draftFiles: DraftMetadataFileEntry[]
  disabled?: boolean
  title?: string
  uploadingSlotKey?: string | null
  onSelectSlotFileId: (slot: SlotKey, fileId: string | null) => void
  onSlotPagesChange?: (slot: SlotKey, pages: number[]) => void
  onUploadSlotFile: (slot: SlotKey, file: File) => Promise<void>
  budgetPlanillaSlot?: (ctx: { revealBudgetDocSource: () => void }) => React.ReactNode
  budgetSheetPresent?: boolean
}

const RubroDocumentationBlock: React.FC<Props> = ({
  tempId,
  slots,
  pdfBaseUrl,
  maxPage,
  fileIds,
  draftFiles,
  disabled,
  title,
  uploadingSlotKey,
  onSelectSlotFileId,
  onSlotPagesChange,
  onUploadSlotFile,
  budgetPlanillaSlot,
  budgetSheetPresent
}) => {
  const resolvedMaxPage =
    typeof maxPage === 'number' && Number.isFinite(maxPage) && maxPage >= 1 ? maxPage : null
  const canPickPages =
    resolvedMaxPage != null && typeof onSlotPagesChange === 'function' && !disabled

  const [subsetChosen, setSubsetChosen] = useState<Partial<Record<SlotKey, boolean>>>({})
  const [fileChosen, setFileChosen] = useState<Partial<Record<SlotKey, boolean>>>({})
  const [reconfigureOpen, setReconfigureOpen] = useState<Partial<Record<SlotKey, boolean>>>({})
  const [budgetShowDocSource, setBudgetShowDocSource] = useState(false)

  useEffect(() => {
    if (budgetSheetPresent) setBudgetShowDocSource(false)
  }, [budgetSheetPresent])

  const revealBudgetDocSource = useCallback(() => {
    setBudgetShowDocSource(true)
    setReconfigureOpen((prev) => ({ ...prev, budget: true }))
  }, [])

  const applySlotMode = (slot: SlotKey, mode: 'full' | 'subset' | 'file') => {
    if (!onSlotPagesChange || !onSelectSlotFileId) return
    if (mode === 'file') {
      onSlotPagesChange(slot, [])
      setSubsetChosen((prev) => ({ ...prev, [slot]: false }))
      setFileChosen((prev) => ({ ...prev, [slot]: true }))
      return
    }
    setFileChosen((prev) => ({ ...prev, [slot]: false }))
    onSelectSlotFileId(slot, null)
    if (mode === 'full' && resolvedMaxPage) {
      onSlotPagesChange(slot, Array.from({ length: resolvedMaxPage }, (_, i) => i + 1))
      setSubsetChosen((prev) => ({ ...prev, [slot]: false }))
      return
    }
    if (mode === 'subset') {
      setSubsetChosen((prev) => ({ ...prev, [slot]: true }))
    }
  }

  const deriveSlotMode = (
    slot: SlotKey,
    pages: number[] | undefined,
    hasFile: boolean,
    hasPages: boolean
  ) => {
    if (hasFile) return 'file'
    if (fileChosen[slot]) return 'file'
    if (!resolvedMaxPage) return hasPages ? 'subset' : 'empty'
    if (isFullPageSelection(pages, resolvedMaxPage)) return 'full'
    if (hasPages || subsetChosen[slot]) return 'subset'
    return 'empty'
  }

  const basePdf = pdfBaseUrl?.trim() || ''

  const pageRows = useMemo(() => buildDocumentationPageRows(slots, fileIds), [slots, fileIds])

  const removeTypeFromPage = (slot: SlotKey, pageNum: number) => {
    if (!onSlotPagesChange) return
    const cur = normalizePageList(slots[slot]) ?? []
    onSlotPagesChange(
      slot,
      cur.filter((p) => p !== pageNum)
    )
  }

  const canEditPageChips = canPickPages && typeof onSlotPagesChange === 'function'

  const slotMetaList = useMemo(
    () =>
      DOCUMENTATION_SLOT_DEFINITIONS.map(({ key, label }) => {
        const pages = slots[key]
        const hasPages = Array.isArray(pages) && pages.length > 0
        const fid = fileIds[key]
        const hasFile = typeof fid === 'string' && fid.trim().length > 0
        const resolved = hasFile || hasPages
        return { key, label, pages, hasPages, fid, hasFile, resolved }
      }),
    [slots, fileIds]
  )

  const resolvedSlots = slotMetaList.filter((s) => s.resolved)
  const orphanSlots = slotMetaList.filter((s) => !s.resolved)

  const renderFileControls = (key: SlotKey, isUploading: boolean) => (
    <div
      className={styles.projectAutofillRubroDocSlotActions}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <select
        className={styles.projectAutofillRubroDocSelect}
        value={fileIds[key] ? String(fileIds[key]) : ''}
        disabled={disabled || isUploading}
        onChange={(e) => {
          const v = e.target.value.trim()
          onSelectSlotFileId(key, v.length ? v : null)
        }}
      >
        <option value="">Archivo del borrador…</option>
        {draftFiles.map((f) => {
          const id = f.id != null ? String(f.id) : ''
          if (!id) return null
          return (
            <option key={id} value={id}>
              {fileEntryLabel(f)}
            </option>
          )
        })}
      </select>
      <RubroDocSlotFileTrigger
        disabled={!!disabled}
        isUploading={isUploading}
        onFile={(file) => void onUploadSlotFile(key, file)}
      />
    </div>
  )

  const fullLabel =
    resolvedMaxPage != null
      ? `PDF del pliego completo (${resolvedMaxPage} ${resolvedMaxPage === 1 ? 'página' : 'páginas'})`
      : 'PDF del pliego completo'

  const compactSummary = (meta: (typeof slotMetaList)[0]): React.ReactNode => {
    const { pages, hasFile, fid } = meta
    const entry = hasFile ? draftFiles.find((f) => String(f.id ?? '') === String(fid)) : undefined
    if (hasFile) {
      if (entry?.storageUrl?.trim()) {
        return (
          <a
            href={entry.storageUrl.trim()}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.projectAutofillRubroDocPageLink}
          >
            {fileEntryLabel(entry)}
          </a>
        )
      }
      return <span>{entry ? fileEntryLabel(entry) : fid}</span>
    }
    if (resolvedMaxPage && isFullPageSelection(pages, resolvedMaxPage)) {
      return (
        <>
          Todo el PDF del pliego ({resolvedMaxPage} {resolvedMaxPage === 1 ? 'página' : 'páginas'})
          {basePdf ? (
            <>
              {' · '}
              <a href={basePdf} target="_blank" rel="noopener noreferrer" className={styles.projectAutofillRubroDocPageLink}>
                Ver PDF
              </a>
            </>
          ) : null}
        </>
      )
    }
    if (pageRows.length > 0) {
      const n = normalizePageList(pages)?.length ?? 0
      return n > 0 ? `Páginas asignadas en el listado superior (${n})` : '—'
    }
    const short = summarizePagesShort(pages)
    return short ? `Páginas: ${short}` : '—'
  }

  const renderSlotEditorContent = (meta: (typeof slotMetaList)[0]) => {
    const { key, pages, hasPages, fid, hasFile } = meta
    const entry = hasFile ? draftFiles.find((f) => String(f.id ?? '') === String(fid)) : undefined
    const uploadKey = `${tempId}:${key}`
    const isUploading = uploadingSlotKey === uploadKey
    const slotMode = deriveSlotMode(key, pages, hasFile, hasPages)
    const selectId = `doc-source-${tempId}-${key}`
    const fileControls = renderFileControls(key, isUploading)

    return (
      <>
        {canPickPages ? (
          <>
            <label className={styles.projectAutofillRubroDocSourceLabel} htmlFor={selectId}>
              ¿Qué querés enviar?
            </label>
            <select
              id={selectId}
              className={styles.projectAutofillRubroDocSourceSelect}
              value={slotMode === 'empty' ? '' : slotMode}
              disabled={disabled || isUploading}
              onChange={(e) => {
                const v = e.target.value
                if (v === 'full' || v === 'subset' || v === 'file') applySlotMode(key, v)
              }}
            >
              <option value="">Seleccioná…</option>
              <option value="full">{fullLabel}</option>
              <option value="subset">Solo algunas hojas del PDF</option>
              <option value="file">Otro archivo (no el PDF del pliego)</option>
            </select>

            {slotMode === 'full' && resolvedMaxPage ? (
              <p className={styles.projectAutofillRubroDocMuted}>
                Se incluye el documento analizado entero.
                {basePdf ? (
                  <>
                    {' '}
                    <a href={basePdf} target="_blank" rel="noopener noreferrer" className={styles.projectAutofillRubroDocPageLink}>
                      Ver PDF
                    </a>
                  </>
                ) : null}
              </p>
            ) : null}

            {slotMode === 'subset' && resolvedMaxPage ? (
              <DocumentationSlotPagePicker
                maxPage={resolvedMaxPage}
                value={pages}
                pdfBaseUrl={pdfBaseUrl}
                showSelectedChips={false}
                onChange={(next) => onSlotPagesChange!(key, next)}
              />
            ) : null}

            {slotMode === 'file' ? (
              <>
                <p className={styles.projectAutofillRubroDocAttachedLine}>Documento adjunto (no es el PDF del pliego recortado).</p>
                {hasFile ? (
                  <div className={styles.projectAutofillRubroDocFilePicked}>
                    {entry?.storageUrl?.trim() ? (
                      <a
                        href={entry.storageUrl.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.projectAutofillRubroDocPageLink}
                      >
                        {fileEntryLabel(entry)}
                      </a>
                    ) : (
                      <span className={styles.projectAutofillRubroDocFileName}>{entry ? fileEntryLabel(entry) : fid}</span>
                    )}
                    <button
                      type="button"
                      className={styles.projectAutofillRubroDocClear}
                      disabled={disabled || isUploading}
                      onClick={() => onSelectSlotFileId(key, null)}
                    >
                      Quitar
                    </button>
                  </div>
                ) : null}
                {fileControls}
              </>
            ) : null}
          </>
        ) : hasFile ? (
          <>
            <p className={styles.projectAutofillRubroDocAttachedLine}>Documento adjunto.</p>
            <div className={styles.projectAutofillRubroDocFilePicked}>
              {entry?.storageUrl?.trim() ? (
                <a
                  href={entry.storageUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.projectAutofillRubroDocPageLink}
                >
                  {fileEntryLabel(entry)}
                </a>
              ) : (
                <span className={styles.projectAutofillRubroDocFileName}>{entry ? fileEntryLabel(entry) : fid}</span>
              )}
              <button
                type="button"
                className={styles.projectAutofillRubroDocClear}
                disabled={disabled || isUploading}
                onClick={() => onSelectSlotFileId(key, null)}
              >
                Quitar
              </button>
            </div>
            {fileControls}
          </>
        ) : hasPages ? (
          <>
            <p className={styles.projectAutofillRubroDocMuted}>
              {pageRows.length > 0 ? 'Las hojas de este tipo están en el listado superior. ' : ''}
              No podemos ajustar hojas sin el total de páginas del PDF. Podés adjuntar otro archivo.
            </p>
            {fileControls}
          </>
        ) : (
          <>
            <p className={styles.projectAutofillRubroDocMuted}>Sin documentación para este tipo.</p>
            {fileControls}
          </>
        )}
      </>
    )
  }

  return (
    <div className={styles.projectAutofillRubroDoc} title={title}>
      <span className={styles.projectAutofillRubroDocTitle}>Documentación por tipo</span>
      <p className={styles.projectAutofillRubroDocNote}>
        Cada fila del listado muestra una página del archivo y qué tipos de documentación nuestro agente le asignó. Una misma
        página puede tener varias etiquetas sin que eso indique un error.
      </p>
      {pageRows.length > 0 ? (
        <div className={styles.projectAutofillRubroDocStack}>
          <span className={styles.projectAutofillRubroDocByPageHeading}>Páginas del archivo de analizado</span>
          <ul className={styles.projectAutofillRubroDocByPageList}>
            {pageRows.map(({ page, types }) => (
              <li key={page} className={styles.projectAutofillRubroDocByPageRow}>
                <span className={styles.projectAutofillRubroDocByPageNum}>
                  {basePdf ? (
                    <a
                      href={`${basePdf}#page=${page}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.projectAutofillRubroDocPageLink}
                    >
                      Página {page}
                    </a>
                  ) : (
                    `Página ${page}`
                  )}
                </span>
                <span className={styles.projectAutofillRubroDocByPageChips}>
                  {types.map((slotKey) => {
                    const def = DOCUMENTATION_SLOT_DEFINITIONS.find((d) => d.key === slotKey)
                    const chipText = def?.label ?? String(slotKey)
                    return (
                      <span key={slotKey} className={styles.projectAutofillRubroDocSlotTypeChip}>
                        {chipText}
                        {canEditPageChips ? (
                          <button
                            type="button"
                            className={styles.projectAutofillRubroDocSlotTypeChipRemove}
                            disabled={disabled}
                            onClick={() => removeTypeFromPage(slotKey, page)}
                            aria-label={`Quitar ${chipText} de la página ${page}`}
                          >
                            ×
                          </button>
                        ) : null}
                      </span>
                    )
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {resolvedSlots.length > 0 ? (
        <>
          <p className={styles.projectAutofillRubroDocSubheading}>Tipos ya definidos</p>
          <ul className={styles.projectAutofillRubroDocCompactList}>
            {resolvedSlots.map((meta) => {
              const panelId = `rubro-doc-panel-${tempId}-${meta.key}`
              const expanded = !!reconfigureOpen[meta.key]
              return (
                <li key={meta.key} className={styles.projectAutofillRubroDocCompactAccordionItem}>
                  <div
                    className={
                      expanded
                        ? `${styles.projectAutofillRubroDocCompactAccordionInner} ${styles.projectAutofillRubroDocCompactAccordionInnerOpen}`
                        : styles.projectAutofillRubroDocCompactAccordionInner
                    }
                  >
                    <div className={styles.projectAutofillRubroDocCompactRow}>
                      <span className={styles.projectAutofillRubroDocCompactKind}>{meta.label}</span>
                      <span className={styles.projectAutofillRubroDocCompactSummary}>{compactSummary(meta)}</span>
                      {canPickPages ? (
                        <span className={styles.projectAutofillRubroDocFileRow}>
                          {meta.hasFile ? (
                            <button
                              type="button"
                              className={styles.projectAutofillRubroDocTextBtn}
                              disabled={!!disabled}
                              onClick={() => onSelectSlotFileId(meta.key, null)}
                            >
                              Quitar archivo
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={styles.projectAutofillRubroDocTextBtn}
                            disabled={!!disabled}
                            aria-expanded={expanded}
                            aria-controls={panelId}
                            onClick={() =>
                              setReconfigureOpen((prev) => {
                                const next = { ...prev }
                                if (next[meta.key]) delete next[meta.key]
                                else next[meta.key] = true
                                return next
                              })
                            }
                          >
                            {expanded ? 'Ocultar opciones' : 'Cambiar origen'}
                          </button>
                        </span>
                      ) : null}
                    </div>
                    {meta.key === 'budget' && budgetPlanillaSlot && !budgetShowDocSource
                      ? budgetPlanillaSlot({ revealBudgetDocSource })
                      : null}
                    {expanded ? (
                      <div
                        id={panelId}
                        className={styles.projectAutofillRubroDocAccordionPanel}
                        role="region"
                        aria-label={`Origen: ${meta.label}`}
                      >
                        {meta.key === 'budget' && budgetPlanillaSlot && !budgetShowDocSource ? (
                          <p className={styles.projectAutofillRubroDocMuted}>
                            Para indicar presupuesto por PDF o archivo, elegí «No usarla» en la tarjeta de arriba.
                          </p>
                        ) : (
                          renderSlotEditorContent(meta)
                        )}
                      </div>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        </>
      ) : null}

      {orphanSlots.length > 0 ? (
        <div
          className={
            pageRows.length > 0 || resolvedSlots.length > 0
              ? styles.projectAutofillRubroDocFullPanels
              : `${styles.projectAutofillRubroDocFullPanels} ${styles.projectAutofillRubroDocFullPanelsFlush}`
          }
        >
          {orphanSlots.map((meta) => (
            <div key={meta.key} className={styles.projectAutofillRubroDocOrphan}>
              {meta.key === 'budget' && budgetPlanillaSlot && !budgetShowDocSource ? null : (
                <p className={styles.projectAutofillRubroDocOrphanTitle}>{meta.label}</p>
              )}
              {meta.key === 'budget' && budgetPlanillaSlot && !budgetShowDocSource ? (
                budgetPlanillaSlot({ revealBudgetDocSource })
              ) : null}
              {meta.key !== 'budget' || !budgetPlanillaSlot || budgetShowDocSource
                ? renderSlotEditorContent(meta)
                : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default RubroDocumentationBlock
